import {Controller} from "@hotwired/stimulus"
import {TabulatorFull as Tabulator} from "tabulator-tables"
import {createConsumer} from "@rails/actioncable"
import GLightbox from 'glightbox';


export default class extends Controller {
  static targets = ["table", "countDisplay", "totalCount", "currentCount"]
  static values = {
    userColumns: Object
  }
  connect() {

    this.populateDropdownOptions();
    this.initializeCheckboxesFromUserSettings();

    document.querySelector('select[name="category"]').addEventListener('change', (e) => {
      this.table.setData();
    });

    const columns = this.getInitialColumns();

    this.table = new Tabulator(this.tableTarget, {
      virtualDom: false,
      height: '100%',
      index:"bo_id",
      resizableRows: false,
      cssClass: "wrap-text-column",
      rowHeight: 95,
      variableHeight: false,
      ajaxURL: "/admin/business_opportunities/data",
      ajaxConfig: {
        method: "GET",
        headers: {
          "Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      },
      progressiveLoad: "scroll",
      progressiveLoadScrollMargin: 500,
      paginationSize: 500,
      filterMode: "remote",
      sortMode: "remote",
      columnDefaults:{
        width:200,
        resizable:false
      },
      columns: columns,
      ajaxRequesting: (url, params) => {
        const filters = this.table.getHeaderFilters();
        const sorters = this.table.getSorters();
        const selectedCategory = document.querySelector('select[name="category"]')?.value;

        params.filters = this.extractFilterParams(filters);
        params.sorters = this.extractSorterParams(sorters);
        if (selectedCategory) {
          params.category = selectedCategory;
        }
        return true;
      },
      ajaxResponse: (url, params, response) => {
        this.currentCountTarget.textContent = response.displayed_bo_count
        this.totalCountTarget.textContent = response.total_bo_count
        return response;
      },
      scrollToRowIfVisible: false,
    });

    this.initializeActionCable();

    [
      "bo_industry",
      "bo_type",
      "contact_type",
      "from",
      "reason",
      "status",
    ].forEach(this.loadColumnOptions.bind(this));

    this.table.on("cellEdited", function(cell){
      var data = cell.getRow().getData();

      var payload = {
        [cell.getField()]: cell.getValue()
      };

      fetch(`/admin/business_opportunities/${data.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      })
        .then(response => response.json())
        .then(data => {
          console.log('Success:', data);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    });

    this.debounce = (func, delay) => {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => func(), delay);
    };

    document.getElementById('select-all').addEventListener('click', () => this.selectAllColumns());
    document.getElementById('unselect-all').addEventListener('click', () => this.unselectAllColumns());
    document.getElementById('dropdown-toggle').addEventListener('click', () => {
      const dropdownMenu = document.getElementById('dropdown-menu');
      dropdownMenu.style.display = dropdownMenu.style.display === 'none' ? 'block' : 'none';
    });
    document.addEventListener('click', (event) => {
      const dropdownMenu = document.getElementById('dropdown-menu');
      const dropdownToggle = document.getElementById('dropdown-toggle');
      if (!dropdownToggle.contains(event.target) && !dropdownMenu.contains(event.target)) {
        dropdownMenu.style.display = 'none';
      }
    });
    document.addEventListener('click', this.closeAllExpandedCells.bind(this));
    this.setupCheckboxListeners();
  }

  disconnect() {
  }

  initializeActionCable() {
    const consumer = createConsumer();

    this.businessThumbnailsChannel = consumer.subscriptions.create("BusinessThumbnailsChannel", {
      connected() {},
      disconnected() {},
      received: (data) => {
        const { business_id, image_path } = data;
        if (this.table) {
          const row = this.table.getRow(business_id);
          if (row) {
            row.update({
              ai_thumbnail: image_path,
            });

            row.reformat();
          }
        }
      }
    });

    this.zendeskUrlUpdateChannel = consumer.subscriptions.create("ZendeskUrlUpdateChannel", {
      connected() {},
      disconnected() {},
      received: (data) => {
        const { business_id, zendesk_url } = data;
        if (this.table) {
          const row = this.table.getRow(business_id);
          if (row) {
            row.update({
              zendesk_url: zendesk_url,
            });
            row.reformat();
          }
        }
      }
    });
  }

  populateDropdownOptions() {
    const dropdownOptions = document.getElementById('dropdown-options');
    const columns = this.getInitialColumns();

    dropdownOptions.innerHTML = '';

    columns.forEach(column => {
      if (column.field) {
        const label = document.createElement('label');
        const checkbox = document.createElement('input');

        checkbox.type = 'checkbox';
        checkbox.value = column.field;
        checkbox.setAttribute('data-column', column.field);

        const labelText = document.createTextNode(` ${column.title}`);

        label.appendChild(checkbox);
        label.appendChild(labelText);
        dropdownOptions.appendChild(label);
      }
    });
  }

  createCurrencyColumn(title, field, width = 200) {
    return {
      title,
      field,
      sorter: "number",
      headerFilter: "input",
      editor: "number",
      width,
      hozAlign: "right",
      cssClass: "numeric-cell",
      formatter: (cell) => {
        const value = cell.getValue();
        if (field === "price" && value < 5000) {
          cell.getElement().style.backgroundColor = "#FDFD96";
        } else {
          cell.getElement().style.backgroundColor = "";
        }
        return `<div style="text-align: right; width: 100%;">${this.formatCurrency(value)}</div>`;
      },
      cellEdited: (cell) => {
        const value = cell.getValue();
        if (field === "price" && value < 5000) {
          cell.getElement().style.backgroundColor = "#FDFD96";
        } else {
          cell.getElement().style.backgroundColor = "";
        }
        cell.getElement().innerHTML = `<div style="text-align: right; width: 100%;">${this.formatCurrency(value)}</div>`;
      }
    };
  }

  createLocationColumn(title, field, width = 200) {
      return {
        title,
        field,
        width,
        sorter: "string",
        headerFilter: "input",
        formatter: (cell, formatterParams, onRendered) => {
          const element = cell.getElement();
          const row_data = cell.getRow().getData();
          var location = cell.getValue();

          if (title == "Location") {
            if (row_data.territory_approximated) {
              element.style.backgroundColor = "#FDFD96";
            }
          }

          if (!location) {
            if (title != "Location") {
              element.style.cursor = "default";
              return
            }

            location = row_data.location;
            element.style.backgroundColor = "#ffb347";
          }

          const url = this.googleMapsUrl(location);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.innerText = location;
          anchor.target = "_blank";
          anchor.style.display = "block";
          anchor.style.width = "100%";
          anchor.style.height = "100%";
          anchor.style.color = "#2161dc";

          return anchor;
        },
        cellClick: function(e, cell) {
          var location = cell.getValue();
          if (!location) {
            return
          }
          const url = this.googleMapsUrl(location);
          window.open(url, '_blank');
        }
      };
  }

  formatCurrency(value) {
    if (value == null || value === 0) {
      return "";
    }

    const locale = Intl.DateTimeFormat().resolvedOptions().locale || 'es-ES';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currencyDisplay: 'symbol',
      currencySign: 'accounting'
    }).format(value);
  }

  toggleColumnsVisibility(event) {
    const selectedColumns = Array.from(event.target.selectedOptions).map(option => option.value);
    const columns = this.table.getColumns();

    columns.forEach(column => {
      if (selectedColumns.includes(column.getField())) {
        column.show();
      } else {
        column.hide();
      }
    });
  }

  updateColumnVisibility(columnName, isVisible) {
    const column = this.table.getColumn(columnName);
    if (isVisible) {
      column.show();
    } else {
      column.hide();
    }
  }

  extractFilterParams(filters) {
    return filters.map(filter => ({
      field: filter.field,
      type: filter.type,
      value: filter.value
    }));

  }

  customHeaderPopup = (e, column, onRendered, fieldName) => {
    let selectedValues = column.getHeaderFilterValue() || [];

    const container = document.createElement("div");
    container.innerHTML = `
    <div class="custom-popup">
      <div class="search-container">
        <i class="fas fa-search search-icon"></i>
        <input type="text" placeholder="Search..." class="search-input">
      </div>
      <div class="checkbox-container select-all">
        <input type="checkbox" id="select-all">
        <label for="select-all">Select All</label>
      </div>
      <div class="checkbox-list"></div>
      <div class="button-container">
        <button class="apply-btn">Apply</button>
        <button class="clear-btn">Clear</button>
      </div>
    </div>
  `;

    const searchInput = container.querySelector('.search-input');
    const selectAllCheckbox = container.querySelector('#select-all');
    const checkboxList = container.querySelector('.checkbox-list');
    const applyButton = container.querySelector('.apply-btn');
    const clearButton = container.querySelector('.clear-btn');

    const options = this.columnOptions[fieldName] || [];
    this.populateCheckboxList(checkboxList, options, selectedValues);

    searchInput.addEventListener('input', (e) => this.filterCheckboxes(checkboxList, e.target.value));

    selectAllCheckbox.addEventListener('change', (e) => {
      const checkboxes = checkboxList.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(cb => cb.checked = e.target.checked);
      selectedValues = this.updateSelectedValues(checkboxList, selectAllCheckbox);
    });

    applyButton.addEventListener('click', () => {
      selectedValues = this.updateSelectedValues(checkboxList, selectAllCheckbox);
      column.setHeaderFilterValue(selectedValues);
    });

    clearButton.addEventListener('click', () => {
      const checkboxes = container.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(cb => cb.checked = false);
      searchInput.value = "";
      selectedValues = this.updateSelectedValues(checkboxList, selectAllCheckbox);
      column.setHeaderFilterValue([]);
    });

    return container;
  }

  loadColumnOptions(fieldName) {
    fetch(`/admin/business_opportunities/field_name/${fieldName}`)
      .then(response => response.json())
      .then(options => {
        this.columnOptions = this.columnOptions || {};
        this.columnOptions[fieldName] = Array.isArray(options) ? options : [];

        const column = this.table.getColumn(fieldName);
        if (column) {
          column.updateDefinition({
            headerPopup: (e, column, onRendered) => this.customHeaderPopup(e, column, onRendered, fieldName)
          });
        }
      });
  }
  populateCheckboxList(checkboxList, options, selectedValues) {
    const safeOptions = Array.isArray(options) ? options : [];

    checkboxList.innerHTML = safeOptions.map(opt => `
    <div class="checkbox-container">
      <input type="checkbox" value="${opt}" id="checkbox-${opt}" ${selectedValues.includes(opt) ? 'checked' : ''}>
      <label for="checkbox-${opt}">${opt}</label>
    </div>
  `).join('');
  }

  filterCheckboxes(checkboxList, searchTerm) {
    const checkboxes = checkboxList.querySelectorAll(".checkbox-container");
    checkboxes.forEach(cb => {
      const label = cb.querySelector("label").textContent.toLowerCase();
      cb.style.display = label.includes(searchTerm.toLowerCase()) ? "" : "none";
    });
  }

  updateSelectedValues(checkboxList, selectAllCheckbox) {
    const checkboxes = checkboxList.querySelectorAll('input[type="checkbox"]');
    const selectedValues = Array.from(checkboxes).filter(cb => cb.checked).map(cb => cb.value);
    selectAllCheckbox.checked = selectedValues.length === checkboxes.length;
    return selectedValues;
  }

  toggleHeaderFilter(column) {
    const headerElement = column.getElement();
    const filterElement = headerElement.querySelector('.tabulator-header-filter');

    if (filterElement) {
      filterElement.classList.toggle('show');
    }

    this.table.redraw();
  }

  extractSorterParams(sorters) {
    return sorters.map(sorter => ({
      field: sorter.field,
      dir: sorter.dir
    }));
  }

  closeAllExpandedCells() {
    const expandedCells = this.tableTarget.querySelectorAll('.tabulator-cell.expanded');
    expandedCells.forEach(cell => this.collapseCell(cell));
  }

  expandCell(cellElement) {
    cellElement.classList.add('expanded');
    const truncatedText = cellElement.querySelector('.truncated-text');
    const fullTextOverlay = cellElement.querySelector('.full-text-overlay');
    if (truncatedText && fullTextOverlay) {
      truncatedText.style.display = 'none';
      fullTextOverlay.style.display = 'block';
    }
  }

  collapseCell(cellElement) {
    cellElement.classList.remove('expanded');
    const truncatedText = cellElement.querySelector('.truncated-text');
    const fullTextOverlay = cellElement.querySelector('.full-text-overlay');
    if (truncatedText && fullTextOverlay) {
      truncatedText.style.display = 'block';
      fullTextOverlay.style.display = 'none';
    }
  }

  formatExpandableCell(cell, field) {
    let value = this.getCellContent(cell, field) || '';

    const formattedValue = Array.isArray(value)
      ? value.join('\n')
      : value;

    const cellElement = cell.getElement();



    if (formattedValue && formattedValue.length > 140) {
      cellElement.classList.remove('non-clickable-header');
    } else {
      cellElement.classList.add('non-clickable-header');
    }

    return `<div class="cell-content">
       <div class="truncated-text">${formattedValue}</div>
       <div class="full-text-overlay" style="display:none;">
         <div class="full-text-content">${formattedValue}</div>
       </div>
       ${formattedValue && formattedValue.length > 140 ? `<img src="${cell.getRow().getData().expand_icon}" class="expand-icon" alt="Expand">` : ''}
     </div>`;
  }

  handleCellClick(e, cell) {
    const value = this.getCellContent(cell, cell.getField());

    const formattedValue = Array.isArray(value)
      ? value.join('\n')
      : value;


    if (formattedValue.length > 140) {
      const cellElement = cell.getElement();
      const isExpanded = cellElement.classList.contains('expanded');

      this.closeAllExpandedCells();

      if (!isExpanded) {
        this.expandCell(cellElement);
      }

      e.stopPropagation();
    }
  }

  editCell(cell) {
    const currentValue = cell.getValue();
    const cellElement = cell.getElement();
    const table = cell.getTable();

    const editContainer = document.createElement('div');
    editContainer.className = 'edit-container full-text-overlay';
    editContainer.style.display = 'block';
    editContainer.style.position = 'fixed';
    editContainer.style.width = '400px';
    editContainer.style.height = '600px';
    editContainer.style.zIndex = '1000';
    editContainer.style.padding = '10px';
    editContainer.style.boxSizing = 'border-box';
    editContainer.style.overflowY = 'auto';
    editContainer.style.backgroundColor = '#f9f9f9';
    editContainer.style.border = '1px solid #ccc';
    editContainer.style.boxShadow = '0 2px 5px rgba(0,0,0,0.1)';

    const textarea = document.createElement('textarea');
    textarea.value = JSON.stringify(currentValue, null, 2);
    textarea.style.width = '100%';
    textarea.style.height = 'calc(100% - 50px)';
    textarea.style.boxSizing = 'border-box';
    textarea.style.marginBottom = '10px';
    textarea.style.resize = 'none';

    const buttonContainer = document.createElement('div');
    buttonContainer.style.display = 'flex';
    buttonContainer.style.justifyContent = 'flex-end';

    const submitButton = document.createElement('button');
    submitButton.textContent = 'Submit';
    submitButton.className = 'submit-button';
    submitButton.style.marginRight = '10px';

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.className = 'cancel-button';

    buttonContainer.appendChild(submitButton);
    buttonContainer.appendChild(cancelButton);

    editContainer.appendChild(textarea);
    editContainer.appendChild(buttonContainer);

    document.body.appendChild(editContainer);

    const adjustTextareaHeight = () => {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, editContainer.clientHeight - 60)}px`;
    };

    adjustTextareaHeight();
    textarea.addEventListener('input', adjustTextareaHeight);

    textarea.focus();

    const scrollTolerance = 200;

    const updateFormPosition = () => {
      const cellRect = cellElement.getBoundingClientRect();
      const tableRect = table.element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      const headerElement = table.element.querySelector('.tabulator-header');
      const headerBottom = headerElement ? headerElement.getBoundingClientRect().bottom : 0;

      let top = Math.max(cellRect.top, headerBottom);
      let left = cellRect.left;

      const maxHeight = Math.min(windowHeight * 0.8, 600);
      editContainer.style.maxHeight = `${maxHeight}px`;

      if (left + editContainer.offsetWidth > windowWidth) {
        left = windowWidth - editContainer.offsetWidth - 10;
      }

      if (top + editContainer.offsetHeight > windowHeight) {
        top = windowHeight - editContainer.offsetHeight - 10;
      }

      if (cellRect.bottom < headerBottom - scrollTolerance || cellRect.top > windowHeight ||
        cellRect.right < tableRect.left || cellRect.left > tableRect.right) {
        cleanup();
        return;
      }

      top = Math.max(top, headerBottom);

      editContainer.style.top = `${top}px`;
      editContainer.style.left = `${left}px`;
    };

    updateFormPosition();

    const scrollHandler = () => {
      requestAnimationFrame(updateFormPosition);
    };

    window.addEventListener('scroll', scrollHandler, true);
    window.addEventListener('resize', scrollHandler);

    submitButton.addEventListener('click', () => {
      try {
        const newValue = JSON.parse(textarea.value);
        cell.setValue(newValue);
        cleanup();
      } catch (error) {
        console.error('Invalid JSON:', error);
        alert('Invalid JSON. Please check your input.');
      }
    });

    cancelButton.addEventListener('click', () => {
      cleanup();
    });

    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.ctrlKey) {
        submitButton.click();
      }
    });

    const cleanup = () => {
      window.removeEventListener('scroll', scrollHandler, true);
      window.removeEventListener('resize', scrollHandler);
      if (editContainer.parentNode) {
        editContainer.parentNode.removeChild(editContainer);
      }
    };

    submitButton.addEventListener('click', cleanup);
    cancelButton.addEventListener('click', cleanup);
  }

  getCellContent(cell, field) {
    return cell.getValue();
  }

  formatAdditionalFeatures(features) {
    if (!features || features.length === 0) return "";

    return features.map(feature => {
      if (typeof feature.feature === 'string') {
        return `<div class="feature-item"><strong>${feature.feature}:</strong> ${feature.description}</div>`;
      }
      else if (feature.feature && typeof feature.feature === 'object' && feature.description && feature.feature.en && feature.description.en) {
        return `<div class="feature-item"><strong>${feature.feature.en}:</strong> ${feature.description.en}</div>`;
      }
      return '';
    }).join('');
  }

  getInitialColumns() {


    const savedVisibility = this.userColumnsValue || {};

    if (Object.keys(savedVisibility).length === 0 ||
      Object.values(savedVisibility).every(value => value === false)) {
      const checkboxes = document.querySelectorAll('#dropdown-options input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = true;
        savedVisibility[checkbox.getAttribute('data-column')] = true;
      });

      this.saveColumnVisibilityToServer(savedVisibility);
    }

    const columnDefinitions = [
      {
        title: "Id",
        field: "bo_id",
        sorter: "string",
        width: 100,
        headerFilter: "input",
        cssClass: "non-clickable-header",
        headerMenu: (e, column) => {
          e.stopPropagation();
          this.toggleHeaderFilter(column);
          return "";
        }
      },
      {
        title: "Counter",
        field: "counter",
        sorter: "number",
        headerFilter: "input",
        width: 120,
        cssClass: "non-clickable-header",
        formatter: function(cell, formatterParams, onRendered) {
          if (cell.getValue() == 1) {
            cell.getElement().style.backgroundColor = "#9fd4a3";
          }
          return cell.getValue();
        },
      },
      {
        title: "Seller Urgency",
        field: "seller_urgency",
        sorter: "float",
        headerFilter: "input",
        cssClass: "non-clickable-header",
        formatter: function(cell) {
          const value = parseFloat(cell.getValue());
          const counter = cell.getRow().getData().counter;

          if (counter < 5) {
            cell.getElement().style.backgroundColor = "#FFFFFF";
            return "";
          }

          let bgColor;
          let text;

          if (value >= 0.7) {
            bgColor = "#B19CD8";
            text = "High";
          } else if (value >= 0.4) {
            bgColor = "#CCB7E5";
            text = "Medium";
          } else {
            bgColor = "#E6D1F2";
            text = "Low";
          }

          cell.getElement().style.backgroundColor = bgColor;
          return text;
        }
      },
      {
        title: "Leadbase",
        field: "categories",
        sorter: "string",
        headerFilter: "input",
        width: 160,
        formatter: function(cell) {
          const categories = cell.getValue();
          return Array.isArray(categories) ? categories.join('<br>') : categories;
        },
        formatterParams: {
          html: true
        },
        cssClass: "non-clickable-header",
      },
      {
        title: "Data Source",
        field: "icon",
        width: 140,
        cssClass: "non-clickable-header",
        formatter: function(cell) {
          const from = cell.getData().from;
          return `
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <img src="${cell.getValue()}" style="width: 24px; height: 24px; object-fit: contain;" alt="icon">
                      <span>${from}</span>
                    </div>
                  `;
        }
      },
      {
        title: "Search Keywords",
        field: "from",
        sorter: "number",
        headerPopup: this.customHeaderPopup,
        width: 200,
        headerFilter: "input",
        formatter: function(cell, formatterParams, onRendered) {
          var value = cell.getValue();
          var listedAt = cell.getData().listed_at;
          var scraperName = cell.getData().scraper_name;

          var displayValue = value;
          if (scraperName) {
            displayValue = scraperName;
          }

          var style = 'word-break: break-word; overflow-wrap: break-word; white-space: normal; display: block; width: 100%; height: 100%; color: #2161dc;';

          if (listedAt) {
            return '<a href="' + listedAt + '" target="_blank" style="' + style + '">' + displayValue + '</a>';
          }
          return '<span style="' + style + '">' + displayValue + '</span>';
        },
        cellClick: function(e, cell) {
          var listedAt = cell.getData().listed_at;
          if (listedAt) {
            window.open(listedAt, '_blank');
          }
        }
      },
      {
        title: "Public Website",
        field: "website_state",
        sorter: "number",
        headerFilter: "input",
        editor: "list",
        width: 160,
        editorParams: {
          values: {
            pending: "Pending",
            preapproved: "Preapproved",
            approved: "Approved",
            rejected: "Rejected"
          }
        },
        formatter: function(cell, formatterParams, onRendered) {
          const value = cell.getValue();
          const styles = {
            pending: { bg: "", text: "inherit" },
            check_llm: { bg: "#0e4bef", text: "white" },
            discard_llm: { bg: "#f80000", text: "white" },
            preapproved_llm: { bg: "#011a5e", text: "white" },
            preapproved: { bg: "#abdee6", text: "inherit" },
            approved: { bg: "#d4f0e0", text: "inherit" },
            rejected: { bg: "#ffc5bf", text: "inherit" }
          };

          const stateDisplayMap = {
            pending: "Pending",
            preapproved: "Preapproved",
            approved: "Approved",
            rejected: "Rejected",
            check_llm: "Check (LLM)",
            discard_llm: "Discard (LLM)",
            preapproved_llm: "Preapproved (LLM)"
          };

          const style = styles[value] || { bg: "", text: "inherit" };

          onRendered(function(){
            cell.getElement().style.backgroundColor = style.bg;
            cell.getElement().style.color = style.text;
          });

          return stateDisplayMap[value] || value;
        },
        cellEdited: function(cell) {
          const newValue = cell.getValue();
          const oldValue = cell.getOldValue();

          if (!newValue || newValue.trim() === '') {
            cell.setValue(oldValue);
            cell.getElement().style.backgroundColor = styles[oldValue]?.bg || "";
            cell.getElement().style.color = styles[oldValue]?.text || "inherit";
            return;
          }

          const row = cell.getRow();
          const rowData = row.getData();
          const businessOpportunityId = rowData.id;
          const websiteState = newValue;
          const selectedCategory = document.querySelector('select[name="category"]')?.value;

          fetch(`/admin/business_opportunities/${businessOpportunityId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify({
              category_states: [{
                category_id: selectedCategory,
                website_state: websiteState
              }]
            })
          })
            .then(response => response.json())
            .then(data => {
              if (!data.success) {
                cell.setValue(oldValue);
                cell.getElement().style.backgroundColor = styles[oldValue]?.bg || "";
                cell.getElement().style.color = styles[oldValue]?.text || "inherit";
              }
            })
            .catch(error => {
              cell.setValue(oldValue);
              cell.getElement().style.backgroundColor = styles[oldValue]?.bg || "";
              cell.getElement().style.color = styles[oldValue]?.text || "inherit";
            });
        }
      },
      {
        title: "Thumbnail Scraper",
        field: "scraped_images",
        formatter: (cell, formatterParams, onRendered) => {
          const rowData = cell.getRow().getData();
          const images = rowData.scraped_images || [];
          const displayImage = images[0] || rowData.public_thumbnail;

          const container = document.createElement("div");
          container.className = "relative w-full h-full group";

          const imageContainer = document.createElement("div");
          imageContainer.className = `block w-full h-full bg-gray-100 gallery-item ${displayImage ? 'cursor-pointer' : 'cursor-default'}`;

          const img = document.createElement("img");
          img.src = displayImage;
          img.className = "w-full h-full object-cover object-center block";
          img.dataset.glightbox = "gallery";
          img.dataset.gallery = "gallery-group";

          imageContainer.href = displayImage;

          img.onerror = () => {
            img.remove();
          };

          imageContainer.appendChild(img);
          container.appendChild(imageContainer);

          if (images.length > 1) {
            const counter = document.createElement("div");
            counter.className = "absolute top-2 right-2 bg-white text-gray-700 text-xs px-2 py-1 rounded-md shadow";
            counter.textContent = images.length;
            imageContainer.appendChild(counter);
          }

          if (displayImage) {
            imageContainer.addEventListener('click', () => {
              const galleryElements = images.length > 0 ?
                images.map(image => ({
                  href: image,
                  type: 'image'
                })) :
                [{
                  href: displayImage,
                  type: 'image'
                }];

              const lightbox = GLightbox({
                elements: galleryElements
              });
              lightbox.open();
            });
          }

          return container;
        },
        width: 180
      },
      {
        title: "Thumbnail",
        field: "thumbnails",
        formatter: (cell, formatterParams, onRendered) => {
          const rowData = cell.getRow().getData();

          const displayImage = rowData.ai_thumbnail ||
            (rowData.scraped_images?.[0]) ||
            rowData.public_thumbnail;

          let galleryImages = [];
          if (rowData.ai_thumbnail) {
            galleryImages = [rowData.ai_thumbnail];
          } else if (rowData.scraped_images?.length > 0) {
            galleryImages = rowData.scraped_images;
          } else if (rowData.public_thumbnail) {
            galleryImages = [rowData.public_thumbnail];
          }

          const container = document.createElement("div");
          container.className = "relative w-full h-full group";

          const imageContainer = document.createElement("div");
          imageContainer.className = `block w-full h-full bg-gray-100 gallery-item ${displayImage ? 'cursor-pointer' : 'cursor-default'}`;

          const img = document.createElement("img");
          img.src = displayImage;
          img.className = "w-full h-full object-cover object-center block";
          img.dataset.glightbox = "gallery";
          img.dataset.gallery = "gallery-group";

          imageContainer.href = displayImage;

          img.onerror = () => {
            img.remove();
          };

          if (galleryImages.length > 1) {
            const counter = document.createElement("div");
            counter.className = "absolute top-2 right-2 bg-white text-gray-700 text-xs px-2 py-1 rounded-md shadow";
            counter.textContent = galleryImages.length;
            container.appendChild(counter);
          }

          // AI button logic remains the same
          const button = document.createElement("button");
          button.className = "absolute bottom-2 right-2 w-8 h-8 bg-white hover:bg-gray-100 rounded-full shadow opacity-0" +
            " group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center";

          const icon = document.createElement("img");
          icon.src = rowData.openai;
          icon.className = "object-contain";
          button.appendChild(icon);

          button.onclick = async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const confirmed = confirm("Are you sure you want to replace the current photo/s with an AI thumbnail?");

            if (confirmed) {
              try {
                const response = await fetch(`/admin/business_opportunities/${rowData.id}/ai_thumbnail_generate`, {
                  method: 'GET',
                  headers: {
                    'Accept': 'application/json'
                  }
                });

                if (response.ok) {
                  console.log('AI thumbnail generated successfully');
                } else {
                  console.error('Failed to generate AI thumbnail');
                }
              } catch (error) {
                console.error('Error generating AI thumbnail:', error);
              }
            }
          };

          imageContainer.appendChild(img);
          container.appendChild(imageContainer);
          container.appendChild(button);

          if (displayImage) {
            imageContainer.addEventListener('click', () => {
              const galleryItems = galleryImages.map(imagePath => ({
                href: imagePath,
                type: 'image'
              }));

              const lightbox = GLightbox({
                elements: galleryItems
              });
              lightbox.open();
            });
          }

          return container;
        },
        width: 140
      },
      {
        title: "Original Title",
        minWidth: "250",
        field: "original_title",
        sorter: "string",
        headerFilter: "input",
        formatter: function(cell, formatterParams, onRendered) {
          const value = cell.getValue();
          const url = cell.getData().url;

          const style = 'word-break: break-word; overflow-wrap: break-word; white-space: normal; display: block; width: 100%; height: 100%; color: #2161dc;';

          if (url) {
            return `<a href="${url}" target="_blank" rel="noreferrer" style="${style}">${value}</a>`;
          }
          return `<span style="${style}">${value}</span>`;
        },
        cellClick: function(e, cell) {
          const url = cell.getData().url;
          if (url) {
            window.open(url, '_blank');
          }
        },
        headerMenu: (e, column) => {
          e.stopPropagation();
          this.toggleHeaderFilter(column);
          return "";
        }
      },
      {
        title: "Title",
        minWidth: "250",
        field: "llm_title",
        sorter: "string",
        headerFilter: "input",
        formatter: function(cell, formatterParams, onRendered) {
          const value = cell.getValue();
          const rowData = cell.getRow().getData();

          return `<a href="/business_opportunities/${rowData.id}"
            target="_blank"
            style="color: #2161dc; text-decoration: none;">${value}</a>`;
        },
        headerMenu: (e, column) => {
          e.stopPropagation();
          this.toggleHeaderFilter(column);
          return "";
        }
      },
      {
        title: "Description",
        field: "text",
        sorter: "string",
        headerFilter: "input",
        minWidth: "250",
        formatter: (cell) => this.formatExpandableCell(cell, "text"),
        cellClick: (e, cell) => this.handleCellClick(e, cell),
      },
      {
        title: "LLM-Description(EN)",
        field: "description_en",
        sorter: "string",
        headerFilter: "input",
        minWidth: "250",
        formatter: (cell) => this.formatExpandableCell(cell, "description_en"),
        cellClick: (e, cell) => this.handleCellClick(e, cell),
      },
      {
        title: "LLM-Description(ES)",
        field: "description_es",
        sorter: "string",
        headerFilter: "input",
        minWidth: "250",
        formatter: (cell) => this.formatExpandableCell(cell, "description_es"),
        cellClick: (e, cell) => this.handleCellClick(e, cell),
      },
      {
        title: "Actions",
        field: "whatsapp",
        sorter: "string",
        headerFilter: "input",
        width: 130,
        formatter: (cell) => {
          const data = cell.getRow().getData();
          const zendesk_url = data.zendesk_url;
          const contact_numbers = data.contact_number;

          let actions = `<div class="flex items-center space-x-2">`;
          if (Array.isArray(contact_numbers) && contact_numbers.length > 0) {
            actions += `
            <button class="whatsapp-button p-0 bg-transparent border-0 cursor-pointer">
              <img src="${data.whatsapp_icon}" width="32" height="32" class="rounded-md">
            </button>
          `;
          }

          if (zendesk_url) {
            actions += `
            <a href="${zendesk_url}" data-turbo="false" target="_blank">
              <img src="${data.zendesk_icon}" width="30" height="30" class="rounded-md">
            </a>
          `;
          }

          actions += `</div>`;
          return actions;
        },
        cellClick: (e, cell) => {
          const target = e.target.closest('.whatsapp-button');
          if (target) {
            e.preventDefault();
            const confirmMessage = "Are you sure you want to send a WhatsApp message?";

            if (confirm(confirmMessage)) {
              const href = `/admin/business_opportunities/${cell.getRow().getData().id}/whatsapp_owner_message`;
              fetch(href, {
                method: 'POST',
                headers: {
                  'X-Requested-With': 'XMLHttpRequest',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'send_whatsapp_message' })
              })
                .then(response => response.json())
                .catch(error => {
                  console.error('Error:', error);
                });
            }
          }
        }
      },
      { title: "Message", field: "message", sorter: "string", headerFilter: "input",
        formatter: function(cell) {
          let cellValue = cell.getValue() || "";
          cellValue = cellValue.replace(/\n/g, "<br>");
          return `<div class="cell-content">
              <span>${cellValue}</span>

            </div>`;
        },
        editor: function(cell, onRendered, success, cancel) {
          const cellValue = cell.getValue() || "";
          const container = document.createElement("div");

          const input = document.createElement("textarea");
          input.value = cellValue;
          input.className = "input-message";
          input.style.width = "100%";
          input.style.marginBottom = "10px";

          const buttonContainer = document.createElement("div");
          buttonContainer.innerHTML = `
              <button class="btn-generate" data-id="${cell.getRow().getData().id}">
                <img src="${cell.getRow().getData().chatgpt_icon}" alt="ai_text" width="32" height="32">
              </button>
            `;

          container.appendChild(input);
          container.appendChild(buttonContainer);

          onRendered(function() {
            input.focus();
            input.select();
          });

          input.addEventListener("change", function() {
            success(input.value);
          });

          input.addEventListener("keydown", function(e) {
            if (e.key === "Escape") {
              cancel();
            }
          });

          container.querySelectorAll('.btn-generate').forEach(button => {
            button.addEventListener('click', (event) => {
              const id = button.getAttribute('data-id');
              fetch(`/admin/business_opportunities/${id}/generate_message`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
              })
                .then(response => response.json())
                .then(data => {
                  console.log('Message generated:', data);

                  const generatedMessage = data.message;
                  input.value = generatedMessage;
                  success(input.value);
                })
                .catch((error) => {
                  console.error('Error:', error);
                });
            });
          });

          return container;
        }
      },
      { title: "Location Scraper", field: "location", sorter: "string", headerFilter: "input", cssClass: "non-clickable-header",},
      this.createLocationColumn("Location", "territory"),
      this.createLocationColumn("Autonomous Community", "autonomous_community", 230),
      this.createLocationColumn("Province", "province"),
      this.createLocationColumn("Municipality", "municipality"),
      { title: "Population", field: "population", sorter: "number", headerFilter: "input", cssClass: "non-clickable-header numeric-cell",},
      this.createCurrencyColumn("Price Original", "price", 150),
      this.createCurrencyColumn("Price", "public_price", 120),
      this.createCurrencyColumn("Monthly Rent", "monthly_rent"),
      this.createCurrencyColumn("Monthly Revenue", "calculated_monthly_revenue"),
      this.createCurrencyColumn("Monthly Profit", "monthly_profit"),
      {
        title: "ROI",
        field: "roi",
        sorter: "number",
        headerFilter: "input",
        width: 110,
        cssClass: "non-clickable-header numeric-cell",
        formatter: function(cell, formatterParams, onRendered) {
          var value = cell.getValue();
          if (value !== null && value !== undefined && value !== "") {
            var numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue !== 0) {
              let val =  Math.round(numValue).toString() + "%";
              return `<div style="text-align: right; width: 100%;">${val}</div>`;
            }
          }
          return "";
        },
      },
      {
        title: "Payback period",
        field: "payback_period",
        sorter: "number",
        headerFilter: "input",
        width: 250,
        cssClass: "non-clickable-header numeric-cell",
        formatter: function(cell, formatterParams, onRendered){
          let value = cell.getValue();
          return value ? value + " months" : "";
        }
      },
      { title: "No of Employees", field: "employee_count", sorter: "number", headerFilter: "input", editor: "number", cssClass: "numeric-cell"  },
      {
        title: "Contact No",
        field: "contact_number",
        sorter: "string",
        headerFilter: "input",
        cssClass: "numeric-cell",
        editor: "input",
        formatter: function(cell) {
          var value = cell.getValue();
          return Array.isArray(value) ? value.join('<br>') : value;
        }
      },
      {
        title: "Time Operational",
        field: "operation_time",
        sorter: "number",
        cssClass: "non-clickable-header numeric-cell",
        formatter: function(cell) {
          var value = cell.getValue();

          if (value == null || value === "") {
            return "";
          }

          if (value === 1) {
            return value + " year";
          } else {
            return value + " years";
          }
        }
      },
      { title: "Type", field: "bo_type", sorter: "string", editor: "input", headerPopup: this.customHeaderPopup, headerFilter: "input" },
      { title: "Industry", field: "industry", sorter: "string", headerPopup: this.customHeaderPopup, headerFilter: "input", cssClass: "non-clickable-header"},
      { title: "Subindustry", field: "sub_industry", sorter: "string", headerPopup: this.customHeaderPopup, headerFilter: "input", cssClass: "non-clickable-header"},
      { title: "Reason", field: "reason", sorter: "string", editor: "input", headerPopup: this.customHeaderPopup, headerFilter: "input" },
      { title: "Contact Type", field: "contact_type", sorter: "string", editor:"list", editorParams: {
          values: {
            not_available: "Not Available",
            agency: "Agency",
            seller: "Seller"
          }
        }
        ,headerPopup: this.customHeaderPopup,
        headerFilter: "input"
      },
      { title: "Email", field: "email", sorter: "string", headerFilter: "input", editor: "input" },
      { title: "Author", field: "author", sorter: "string", headerFilter: "input" , cssClass: "non-clickable-header",},
      { title: "Blacklist", field: "blacklist", sorter: "string", headerFilter: "input" , cssClass: "non-clickable-header",},
      { title: "First Scraped", field: "first_scraped_at", sorter: "date", headerFilter: "input", width: 150, cssClass: "non-clickable-header", },
      { title: "Last Scraped", field: "last_scraped_at", sorter: "date", headerFilter: "input", width: 140, cssClass: "non-clickable-header", },
      {
        title: "Attachment",
        field: "attachment",
        formatter: function(cell, formatterParams, onRendered) {
          const data = cell.getRow().getData();
          const attachments = data.attachments || [];

          let attachmentHtml = attachments.map(attachment => `
              <div class="attachment-item">
                <a href="${attachment.url}" target="_blank">${attachment.name}</a>
              </div>
            `).join("");
          return `<div class="attachment-list">
              ${attachmentHtml}
              <button class="btn-add-attachment">+ Add</button>
            </div>`;
        },
        editor: function(cell, onRendered, success, cancel) {
          const container = document.createElement("div");
          container.className = "attachment-editor";

          const data = cell.getRow().getData();
          const attachments = data.attachments || [];

          container.innerHTML = `
              <input type="file" multiple class="file-input" />
              <hr>
              <br>
              <button class="btn-upload" disabled>Upload</button>
              <button class="btn-cancel">Cancel</button>
              <hr>
              ${attachments.map(attachment => `
                <div class="attachment-item">
                  <a href="${attachment.url}" target="_blank">${attachment.name}</a>
                  <a href="#" class="btn-delete" data-id="${attachment.id}">X</a>
                </div>
              `).join('')}
              <br>

            `;

          const fileInput = container.querySelector(".file-input");
          const submitButton = container.querySelector(".btn-upload");
          const cancelButton = container.querySelector(".btn-cancel");

          fileInput.addEventListener("change", function() {
            submitButton.disabled = fileInput.files.length === 0;
          });

          submitButton.addEventListener("click", function() {
            const files = fileInput.files;
            const formData = new FormData();

            formData.append("business_opportunity[id]", cell.getRow().getData().id);
            for (let i = 0; i < files.length; i++) {
              formData.append("business_opportunity[attachments][]", files[i]);
            }

            fetch(`/admin/business_opportunities/${cell.getRow().getData().id}/update_attachments`, {
              method: "PATCH",
              body: formData,
            })
              .then(response => {
                if (!response.ok) {
                  throw new Error('Network response was not ok');
                }
                return response.json();
              })
              .then(data => {
                cell.getRow().update({
                  attachments: data.attachments
                });
                cancel();
              })
              .catch(error => {
                console.error('Upload error:', error);
                cancel();
              });
          });

          container.addEventListener('click', (event) => {
            if (event.target.classList.contains('btn-delete')) {
              event.preventDefault();

              const attachmentId = event.target.getAttribute('data-id');
              const rowId = cell.getRow().getData().id;

              fetch(`/admin/business_opportunities/${rowId}/destroy_attachment/?attachment_id=${attachmentId}`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
              })
                .then(response => {
                  if (!response.ok) {
                    throw new Error('Network response was not ok');
                  }
                  return response.json();
                })
                .then(data => {
                  cell.getRow().update({
                    attachments: data.attachments
                  });
                  cancel();
                })
                .catch(error => {
                  console.error('Delete error:', error);
                });
            }
          });

          cancelButton.addEventListener("click", function() {
            cancel();
          });

          onRendered(function() {
            fileInput.focus();
          });

          return container;
        }
      },
      { title: "Status", field: "status", sorter: "string", headerPopup: this.customHeaderPopup, headerFilter: "input", editor: "list",
        editorParams: {
          values: {
            active: "active",
            inactive: "inactive",
          }
        },
      },
      { title: "Comment", field: "comment", sorter: "string", headerFilter: "input", editor: "input" }
    ];

    return columnDefinitions.map(column => ({
      ...column,
      visible: savedVisibility[column.field] !== false
    }));

  }

  initializeCheckboxesFromUserSettings() {
    const savedVisibility = this.userColumnsValue || {};
    const checkboxes = document.querySelectorAll('#dropdown-options input[type="checkbox"]');

    if (Object.keys(savedVisibility).length === 0 ||
      Object.values(savedVisibility).every(value => value === false)) {
      checkboxes.forEach(checkbox => {
        checkbox.checked = true;
      });
    } else {
      checkboxes.forEach(checkbox => {
        const columnName = checkbox.getAttribute('data-column');
        checkbox.checked = savedVisibility[columnName] === undefined || savedVisibility[columnName] !== false;
      });
    }
  }

  getAllColumnVisibility() {
    const checkboxes = document.querySelectorAll('#dropdown-options input[type="checkbox"]');
    const visibilityState = {};

    checkboxes.forEach(checkbox => {
      const columnName = checkbox.getAttribute('data-column');
      visibilityState[columnName] = checkbox.checked;
    });

    return visibilityState;
  }

  saveColumnVisibilityToServer(visibilityState) {
    const allVisibility = this.getAllColumnVisibility();

    fetch('/admin/business_opportunities/update_grid_columns', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ grid_columns: allVisibility })
    })
      .then(response => response.json())
      .catch(error => console.error('Error saving column visibility:', error));
  }

  setupCheckboxListeners() {
    const checkboxes = document.querySelectorAll('#dropdown-options input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const columnName = checkbox.getAttribute('data-column');
        const isVisible = checkbox.checked;

        if (this.table) {
          const column = this.table.getColumn(columnName);
          if (column) {
            if (isVisible) {
              column.show();
            } else {
              column.hide();
            }
          }
        }

        this.saveColumnVisibilityToServer();
      });
    });
  }

  selectAllColumns() {
    const checkboxes = document.querySelectorAll('#dropdown-options input[type="checkbox"]');

    checkboxes.forEach(checkbox => {
      checkbox.checked = true;
      const columnName = checkbox.getAttribute('data-column');

      if (this.table) {
        const column = this.table.getColumn(columnName);
        if (column) column.show();
      }
    });

    this.saveColumnVisibilityToServer();
  }

  unselectAllColumns() {
    const checkboxes = document.querySelectorAll('#dropdown-options input[type="checkbox"]');

    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
      const columnName = checkbox.getAttribute('data-column');

      if (this.table) {
        const column = this.table.getColumn(columnName);
        if (column) column.hide();
      }
    });

    this.saveColumnVisibilityToServer();
  }

  googleMapsUrl(location) {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale || 'es-ES';
    let domain;
    switch (locale) {
      case "es-ES":
        domain = "www.google.es";
        break;
      default:
        domain = "www.google.com";
        break;
    }
    return `https://${domain}/maps?q=${location}&ll=39.7686614,-7.7314192&z=6.33`;
  }
}
